import type { ReactNode } from 'react';
import type { EventRef, FolderPath, NodeDefinitionRef, QueueRef, ZoneRef } from '@norscom/nap-shared';

import { ViewPlaceholder } from './ViewPlaceholder';

/** What every reference in a details read carries: an id, a name (nullable for some types), and its zone if it is in one. */
export interface ReferenceValue {
  readonly id: number;
  readonly name: string | null;
  readonly zone?: { readonly name: string } | null;
}

export interface ViewReferenceProps<R extends ReferenceValue = ReferenceValue> {
  /** `null`: no reference, or one that does not resolve or the user may not see; the read does not tell these apart. */
  readonly value: R | null | undefined;
  readonly placeholder?: ReactNode;
}

/**
 * A reference to another object: its name and zone, or its id when it has no name.
 * The typed View<X>Reference components wrap this; each is where a link to X's
 * details goes once X has a details pane.
 */
export const ViewReference = ({ noun, value, placeholder }: ViewReferenceProps & { readonly noun: string }) => {
  if (value === null || value === undefined) return <ViewPlaceholder placeholder={placeholder} />;
  if (value.name === null || value.name === '') return <>{`${noun} #${value.id}`}</>;
  return <>{value.zone ? `${value.name} (${value.zone.name})` : value.name}</>;
};

export const ViewZoneReference = (props: ViewReferenceProps<ZoneRef>) => <ViewReference noun="zone" {...props} />;
export const ViewNodeDefinitionReference = (props: ViewReferenceProps<NodeDefinitionRef>) => <ViewReference noun="node definition" {...props} />;
export const ViewEventReference = (props: ViewReferenceProps<EventRef>) => <ViewReference noun="event" {...props} />;
export const ViewQueueReference = (props: ViewReferenceProps<QueueRef>) => <ViewReference noun="queue" {...props} />;
/** One folder of a path. Its zone is not shown: a path is always in its object's zone. */
export const ViewFolderReference = ({ value, ...props }: ViewReferenceProps<FolderPath>) => (
  <ViewReference noun="folder" value={value && { id: value.id, name: value.name }} {...props} />
);
